import React, { type ComponentType, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import type { AuthenticationAccessRight } from "src/authentication/objects/AuthenticationToken";
import { IntegrationDetailWisepops } from "src/integrations/components/IntegrationDetailWisepops";
import { IntegrationDetailShopifyCheckout } from "src/integrations/components/shopify/IntegrationDetailShopifyCheckout";
import { IntegrationDetailShopifyCustomer } from "src/integrations/components/shopify/IntegrationDetailShopifyCustomer";
import { IntegrationDetailShopifyForm } from "src/integrations/components/shopify/IntegrationDetailShopifyForm";
import { IntegrationDetailShopifyOrder } from "src/integrations/components/shopify/IntegrationDetailShopifyOrder";
import { ShopifyLogo } from "src/integrations/components/ShopifyLogo";
import { WisepopsLogo } from "src/integrations/components/WisepopsLogo";
import IntegrationAccessRight from "src/integrations/enums/IntegrationAccessRight";
import type {
  IIntegrationFormData,
  integrationToIntegrationData,
} from "src/integrations/forms/IIntegrationFormData";
import {
  integrationDataToIntegrationWisepops,
  integrationToIntegrationDataWisepops,
} from "src/integrations/forms/IIntegrationFormDataWisepops";
import type { IIntegrationFormImplProps } from "src/integrations/forms/IIntegrationFormImplProps";
import { IntegrationFormWisepops } from "src/integrations/forms/IntegrationFormWisepops";
import {
  integrationDataToIntegrationShopifyCheckout,
  integrationToIntegrationDataShopifyCheckout,
} from "src/integrations/forms/shopify/IntegrationFormDataShopifyCheckout";
import {
  integrationDataToIntegrationShopifyCustomer,
  integrationToIntegrationDataShopifyCustomer,
} from "src/integrations/forms/shopify/IntegrationFormDataShopifyCustomer";
import {
  integrationDataToIntegrationShopifyForm,
  integrationToIntegrationDataShopifyForm,
} from "src/integrations/forms/shopify/IntegrationFormDataShopifyForm";
import {
  integrationDataToIntegrationShopifyOrder,
  integrationToIntegrationDataShopifyOrder,
} from "src/integrations/forms/shopify/IntegrationFormDataShopifyOrder";
import { IntegrationFormShopifyCheckout } from "src/integrations/forms/shopify/IntegrationFormShopifyCheckout";
import { IntegrationFormShopifyCustomer } from "src/integrations/forms/shopify/IntegrationFormShopifyCustomer";
import { IntegrationFormShopifyForm } from "src/integrations/forms/shopify/IntegrationFormShopifyForm";
import { IntegrationFormShopifyOrder } from "src/integrations/forms/shopify/IntegrationFormShopifyOrder";
import type { Integration } from "src/integrations/objects/Integration";
import type { IntegrationType } from "src/integrations/objects/IntegrationType";

export interface IntegrationConfig {
  label: ReactNode;
  typeLabel?: ReactNode;
  LogoClass: ComponentType;
  DetailClass?: ComponentType<{ integration: Integration }>;
  FormClass: ComponentType<IIntegrationFormImplProps>;
  isMerchantGroupUpdatable: boolean;
  isMerchantBased: boolean;
  formDataToObject: (
    integrationType: IntegrationType,
    integrationData: Partial<IIntegrationFormData>,
    integrationUuid?: string,
  ) => Partial<Integration>;
  objectToFormData: (
    integrationType: IntegrationType,
    integration: Integration,
  ) => Partial<IIntegrationFormData>;
  configPermission?: AuthenticationAccessRight;
  disabled?: boolean;
}

export const INTEGRATIONS: Record<IntegrationType, IntegrationConfig> = {
  SHOPIFY_CHECKOUT: {
    label: <FormattedMessage id="integrations.shopifyCheckouts" />,
    typeLabel: <FormattedMessage id="integrations.shopifyCheckouts.short" />,
    LogoClass: ShopifyLogo,
    DetailClass:
      IntegrationDetailShopifyCheckout as IntegrationConfig["DetailClass"],
    FormClass: IntegrationFormShopifyCheckout,
    isMerchantGroupUpdatable: true,
    isMerchantBased: false,
    formDataToObject: integrationDataToIntegrationShopifyCheckout,
    objectToFormData:
      integrationToIntegrationDataShopifyCheckout as typeof integrationToIntegrationData,
    configPermission: IntegrationAccessRight.CONFIGURE_SHOPIFY,
  },
  SHOPIFY_FORM: {
    label: <FormattedMessage id="integrations.shopifyForms" />,
    typeLabel: <FormattedMessage id="integrations.shopifyForms.short" />,
    LogoClass: ShopifyLogo,
    DetailClass:
      IntegrationDetailShopifyForm as IntegrationConfig["DetailClass"],
    FormClass: IntegrationFormShopifyForm,
    isMerchantGroupUpdatable: true,
    isMerchantBased: false,
    formDataToObject: integrationDataToIntegrationShopifyForm,
    objectToFormData:
      integrationToIntegrationDataShopifyForm as typeof integrationToIntegrationData,
    configPermission: IntegrationAccessRight.CONFIGURE_SHOPIFY,
    disabled: true,
  },
  SHOPIFY_CUSTOMER: {
    label: <FormattedMessage id="integrations.shopifyCustomer" />,
    typeLabel: <FormattedMessage id="integrations.shopifyCustomer.short" />,
    LogoClass: ShopifyLogo,
    DetailClass:
      IntegrationDetailShopifyCustomer as IntegrationConfig["DetailClass"],
    FormClass: IntegrationFormShopifyCustomer,
    isMerchantGroupUpdatable: true,
    isMerchantBased: false,
    formDataToObject: integrationDataToIntegrationShopifyCustomer,
    objectToFormData:
      integrationToIntegrationDataShopifyCustomer as typeof integrationToIntegrationData,
    configPermission: IntegrationAccessRight.CONFIGURE_SHOPIFY,
  },
  SHOPIFY_ORDER: {
    label: <FormattedMessage id="integrations.shopifyOrder" />,
    typeLabel: <FormattedMessage id="integrations.shopifyOrder.short" />,
    LogoClass: ShopifyLogo,
    DetailClass:
      IntegrationDetailShopifyOrder as IntegrationConfig["DetailClass"],
    FormClass: IntegrationFormShopifyOrder,
    isMerchantGroupUpdatable: true,
    isMerchantBased: false,
    formDataToObject: integrationDataToIntegrationShopifyOrder,
    objectToFormData:
      integrationToIntegrationDataShopifyOrder as typeof integrationToIntegrationData,
    configPermission: IntegrationAccessRight.CONFIGURE_SHOPIFY,
  },
  WISEPOPS: {
    label: <FormattedMessage id="integrations.wisepops" />,
    LogoClass: WisepopsLogo,
    DetailClass: IntegrationDetailWisepops as IntegrationConfig["DetailClass"],
    FormClass: IntegrationFormWisepops,
    isMerchantGroupUpdatable: false,
    isMerchantBased: true,
    formDataToObject: integrationDataToIntegrationWisepops,
    objectToFormData:
      integrationToIntegrationDataWisepops as typeof integrationToIntegrationData,
    configPermission: IntegrationAccessRight.CONFIGURE_WISEPOPS,
  },
};
